import { ReactNode, forwardRef, useImperativeHandle, useRef } from 'react';
import { cx } from '@linaria/atomic';
import { CommonHTMLButtonAttributes } from '@frontend/types';
import { NakedButton } from '../../naked/naked-button.component';
import type { Placement } from '../hint/get-position';
import { useTooltip } from '../tooltip/use-tooltip';
import { getActionButtonClassName, getPositionStyle } from './action-button.styles';

interface PositionProps {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

interface ButtonProps extends CommonHTMLButtonAttributes, PositionProps {
  /**
   * If `true`, the button is grayed out and unclickable.
   */
  disabled?: boolean;
  /**
   * If `true`, shadows are applied to the button, and the button has `absolute` positioning. Use `top`, `right`, `left`,
   * and `bottom` to position the button.
   */
  floating?: boolean;
  size?: 'medium' | 'large';
  trackingId?: string;
  label?: ReactNode;
  hoverLabelPlacement?: Placement;
  children?: ReactNode;
}

// eslint-disable-next-line react/display-name
export const ActionButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, disabled, floating = false, onClick, size = 'medium', label, hoverLabelPlacement, ...props }, ref) => {
    const { top, right, bottom, left, className: externalClassName, ...rest } = props;
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const { Tooltip, tooltipProps, triggerProps } = useTooltip({
      placement: hoverLabelPlacement,
    });

    /**
     * Simply yields the buttonRef to the parent component.
     * Doing this allows us to merge refs with a predictable API.
     */
    useImperativeHandle(ref, () => buttonRef.current!);

    const className = cx(getActionButtonClassName({ floating, size }), externalClassName);
    const positionStyle = floating ? getPositionStyle({ top, right, bottom, left }) : undefined;
    return (
      <NakedButton
        className={className}
        style={positionStyle}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        {...rest}
        ref={(node) => {
          /** Merge the forwarded ref with the tooltip ref */
          buttonRef.current = node;
          triggerProps.ref(node);
        }}
      >
        <>{children}</>
        {label && <Tooltip {...tooltipProps}>{label}</Tooltip>}
      </NakedButton>
    );
  }
);
